import { useEffect, useRef, useState } from "react";

export type SSEEvent = {
	id: string;
	type: string;
	payload: unknown;
};

type EventStreamStatus = "connecting" | "open" | "closed";

type UseEventStreamResult = {
	events: SSEEvent[];
	status: EventStreamStatus;
	error: string | null;
};

export function useEventStream(
	workspaceSlug: string,
	onSessionExpired?: () => void,
): UseEventStreamResult {
	const [events, setEvents] = useState<SSEEvent[]>([]);
	const [status, setStatus] = useState<EventStreamStatus>("connecting");
	const [error, setError] = useState<string | null>(null);
	const lastEventIdRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		const url = `/api/tenant/${workspaceSlug}/events`;
		const source = new EventSource(url, { withCredentials: true });

		source.onopen = () => {
			setStatus("open");
			setError(null);
		};

		source.onmessage = (e: MessageEvent) => {
			try {
				const data = JSON.parse(e.data as string) as {
					type: string;
					payload: unknown;
				};
				const event: SSEEvent = {
					id: e.lastEventId,
					type: data.type,
					payload: data.payload,
				};
				lastEventIdRef.current = e.lastEventId;
				setEvents((prev) => [...prev, event]);
			} catch {
				// Ignore unparseable messages (e.g. heartbeats)
			}
		};

		source.onerror = () => {
			setStatus("connecting");
			setError("Connection lost, reconnecting…");
		};

		source.addEventListener("session-expired", () => {
			source.close();
			setStatus("closed");
			onSessionExpired?.();
		});

		return () => {
			source.close();
			setStatus("closed");
		};
	}, [workspaceSlug, onSessionExpired]);

	return { events, status, error };
}
